// frontend/src/components/AuthCallbackHandler.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, EventType, InteractionType } from '@azure/msal-browser';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { API_ENDPOINTS } from '../config';
import { API_SCOPE, SILENT_REDIRECT_URI } from '../authConfig';
import { useUser } from '../UserContext';
import { normalizeTierName, isConsultantTier } from '../utils/tierHelpers';
import { triggerAuthRecovery } from '../utils/authRecovery';
import { recoverWithInteractiveLogin } from '../utils/authInteractiveFallback';
import { trackAuthTelemetry } from '../utils/authTelemetry';

/**
 * AuthCallbackHandler - Single Source of Truth for OAuth Redirects
 * 
 * This component is the ONLY place that processes auth callbacks from Azure.
 * All other components (LoginPage, RegistrationPage, etc.) simply call loginRedirect
 * and trust this handler to route appropriately after authentication.
 * 
 * Critical: Waits for inProgress === InteractionStatus.None before any token operations
 */
export default function AuthCallbackHandler() {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const { setProfiles } = useUser();
  
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState([]);
  const isProcessingRef = useRef(false); // Prevent double-execution (React StrictMode)
  const redirectAccountRef = useRef(null); // Account captured from LOGIN_SUCCESS event
  const waitingForMsalSinceRef = useRef(null);

  // Subscribe to MSAL events to capture the exact account from the redirect.
  // This fires during inProgress===handleRedirect, before we process anything,
  // giving us the authoritative account without relying on getActiveAccount().
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event.interactionType === InteractionType.Redirect &&
        event.payload?.account
      ) {
        console.log('[AuthCallback] Captured redirect account from event:', event.payload.account.username);
        redirectAccountRef.current = event.payload.account;
      }
    });
    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  const addDebugLog = (message, data = {}) => {
    const timestamp = new Date().toISOString();
    const logEntry = `[${timestamp}] ${message}`;
    console.log(logEntry, data);
    setDebugInfo(prev => [...prev, { message, data, timestamp }]);
  };

  useEffect(() => {
    async function handleCallback() {
      // Guard against double-execution from React StrictMode or rapid re-renders
      if (isProcessingRef.current) return;
      
      try {
        addDebugLog('[AuthCallback] Starting', { 
          inProgress, 
          isAuthenticated,
          accountCount: accounts.length
        });
        
        // ============================================================
        // STEP 1: Wait for MSAL to complete redirect processing
        // This is CRITICAL - never call token methods while inProgress
        // ============================================================
        if (inProgress !== InteractionStatus.None) {
          if (!waitingForMsalSinceRef.current) {
            waitingForMsalSinceRef.current = Date.now();
          }

          const waitDurationMs = Date.now() - waitingForMsalSinceRef.current;
          const hasUsableSession = isAuthenticated || (accounts?.length || 0) > 0;

          if (hasUsableSession && waitDurationMs >= 8000) {
            addDebugLog('[AuthCallback] MSAL wait exceeded threshold - failing forward to auth/loading', {
              inProgress,
              waitDurationMs,
              accountCount: accounts?.length || 0,
              isAuthenticated
            });
            trackAuthTelemetry('auth_redirect_timeout', {
              inProgress,
              waitDurationMs,
              accountCount: accounts?.length || 0,
              fallback: 'auth_loading'
            });
            navigate('/auth/loading', { replace: true, state: { forceFetch: true } });
            return;
          }

          addDebugLog('[AuthCallback] Waiting for MSAL to complete...', { inProgress, waitDurationMs });
          return; // Wait for next render when MSAL is ready
        }

        waitingForMsalSinceRef.current = null;
        
        // ============================================================
        // STEP 2: Verify authentication succeeded
        // ============================================================
        if (!isAuthenticated) {
          throw new Error('Authentication failed - no active account after redirect');
        }
        
        // Mark as processing to prevent duplicate execution
        isProcessingRef.current = true;
        
        // ============================================================
        // STEP 3: Get the account that just completed this OAuth redirect.
        // Priority:
        //   1. redirectAccountRef - captured from LOGIN_SUCCESS event payload
        //      (the only reliable source when multiple accounts are cached)
        //   2. getActiveAccount() - reliable when only one account exists
        //   3. Error - never guess from accounts array
        // ============================================================
        let account = redirectAccountRef.current || instance.getActiveAccount();
        if (!account) {
          throw new Error('No account found from redirect - could not determine which account completed OAuth');
        }
        // Ensure this account is set as active for all subsequent operations
        instance.setActiveAccount(account);
        
        addDebugLog('[AuthCallback] Account authenticated', {
          username: account.username,
          name: account.name,
          oid: account.localAccountId
        });
        
        // ============================================================
        // STEP 4: Acquire token silently (NOW SAFE - MSAL finished)
        // ============================================================
        addDebugLog('[AuthCallback] Acquiring access token...');
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: [API_SCOPE],
          account: account,
          redirectUri: SILENT_REDIRECT_URI
        });
        
        addDebugLog('[AuthCallback] Token acquired successfully');
        
        // ============================================================
        // STEP 5: Determine user flow from session storage
        // ============================================================
        const needsOnboarding = sessionStorage.getItem('needsOnboarding');
        const invitationToken = sessionStorage.getItem('invitationToken');
        const postAuthRoute = sessionStorage.getItem('postAuthRoute');
        const selectedAccountType = sessionStorage.getItem('selectedAccountType');
        
        addDebugLog('[AuthCallback] Flow flags', {
          needsOnboarding,
          invitationToken: invitationToken ? 'present' : 'none',
          postAuthRoute,
          selectedAccountType
        });
        
        // ============================================================
        // STEP 6: Handle invitation first (highest priority)
        // ============================================================
        if (invitationToken) {
          addDebugLog('[AuthCallback] Processing invitation flow...');
          await handleInvitationFlow(invitationToken, account, tokenResponse, addDebugLog, navigate, setProfiles);
          return;
        }
        
        // ============================================================
        // STEP 7: Handle custom registration redirect
        // ============================================================
        if (postAuthRoute) {
          addDebugLog('[AuthCallback] Custom post-auth route', { postAuthRoute });
          sessionStorage.removeItem('postAuthRoute');
          navigate(postAuthRoute, { replace: true });
          return;
        }
        
        // ============================================================
        // STEP 8: Handle new user onboarding
        // ============================================================
        if (needsOnboarding === 'true') {
          const normalizedTier = normalizeTierName(selectedAccountType);
          const isConsultant = isConsultantTier(normalizedTier);
          const onboardingRoute = isConsultant ? '/onboarding/consultant' : '/onboarding/nonprofit';
          
          addDebugLog('[AuthCallback] New user - routing to onboarding', { 
            route: onboardingRoute,
            accountType: normalizedTier 
          });
          
          navigate(onboardingRoute, { replace: true });
          return;
        }
        
        // ============================================================
        // STEP 9: Existing user - load profile and route
        // ============================================================
        addDebugLog('[AuthCallback] Existing user - routing to auth/loading');
        navigate('/auth/loading', { replace: true });
        
      } catch (err) {
        isProcessingRef.current = false; // Allow retry on error
        console.error('[AuthCallback] Error:', err);
        addDebugLog('[AuthCallback] ERROR', { 
          message: err.message, 
          stack: err.stack 
        });

        const isRecoverableAccountError =
          typeof err?.message === 'string' &&
          (err.message.includes('No account found from redirect') || err.message.includes('no active account after redirect'));

        const isRecoverableSilentTimeout =
          err?.errorCode === 'monitor_window_timeout' ||
          err?.errorCode === 'token_acquisition_timeout' ||
          (typeof err?.message === 'string' && err.message.toLowerCase().includes('monitor_window_timeout'));

        // Stale server-side session: silent token acquisition returns
        // interaction_required / AADSTS160021. This must be recovered with a
        // forced interactive sign-in, otherwise the callback dead-ends and loops.
        const isInteractionRequired =
          err?.errorCode === 'interaction_required' ||
          err?.errorCode === 'login_required' ||
          err?.name === 'InteractionRequiredAuthError' ||
          (typeof err?.message === 'string' &&
            (err.message.includes('AADSTS160021') ||
              err.message.toLowerCase().includes('interaction_required') ||
              err.message.toLowerCase().includes('login_required')));

        if (isRecoverableSilentTimeout) {
          trackAuthTelemetry('auth_silent_timeout', {
            errorCode: err?.errorCode,
            message: err?.message,
            inProgress,
            accountCount: accounts?.length || 0
          });
        }

        if (!isRecoverableAccountError && !isRecoverableSilentTimeout && !isInteractionRequired) {
          trackAuthTelemetry('auth_login_error', {
            errorCode: err?.errorCode,
            message: err?.message,
            inProgress,
            accountCount: accounts?.length || 0
          });
        }

        if (isInteractionRequired) {
          const recovered = await recoverWithInteractiveLogin({
            instance,
            error: err,
            account: redirectAccountRef.current || instance.getActiveAccount(),
            reason: 'callback_interaction_required'
          });
          if (recovered) {
            return;
          }
        }

        if (isRecoverableAccountError || isRecoverableSilentTimeout) {
          const recovered = await triggerAuthRecovery({
            instance,
            navigate,
            reason: isRecoverableSilentTimeout ? 'callback_silent_timeout' : 'callback_account_mismatch'
          });
          if (recovered) {
            return;
          }
        }

        setError(err.message);
        setStatus('error');
      }
    }
    
    handleCallback();
  }, [inProgress, isAuthenticated, accounts, instance, navigate, setProfiles]);
  
  // ============================================================
  // STEP 10: Timeout protection (10 seconds)
  // ============================================================
  useEffect(() => {
    const timer = setTimeout(() => {
      if (status === 'processing') {
        console.error('[AuthCallback] Timeout after 10s');
        addDebugLog('[AuthCallback] TIMEOUT after 10 seconds');

        const hasUsableSession = isAuthenticated || (accounts?.length || 0) > 0;
        if (hasUsableSession) {
          trackAuthTelemetry('auth_redirect_timeout', {
            inProgress,
            accountCount: accounts?.length || 0,
            fallback: 'auth_loading_timeout'
          });
          navigate('/auth/loading', { replace: true, state: { forceFetch: true } });
          return;
        }

        triggerAuthRecovery({
          instance,
          navigate,
          reason: 'callback_timeout'
        }).then((recovered) => {
          if (!recovered) {
            setError('Authentication is taking too long. Please try again.');
            setStatus('error');
          }
        });
      }
    }, 10000);
    
    return () => clearTimeout(timer);
  }, [status, instance, navigate, isAuthenticated, accounts, inProgress]);
  
  // ============================================================
  // UI States
  // ============================================================
  
  if (status === 'processing') {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh"
        sx={{ background: 'linear-gradient(135deg, #173457 0%, #1e4573 100%)' }}
      >
        <CircularProgress size={60} sx={{ color: '#fcdf68', mb: 3 }} />
        <Typography sx={{ fontSize: '1.2rem', color: '#fff', fontWeight: 500 }}>
          Completing sign in...
        </Typography>
        <Typography sx={{ mt: 1, fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.8)' }}>
          {inProgress !== InteractionStatus.None 
            ? 'Waiting for authentication...' 
            : 'Processing your account...'}
        </Typography>
        
        {/* Debug info in development */}
        {process.env.NODE_ENV === 'development' && debugInfo.length > 0 && (
          <Box sx={{ mt: 4, p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1, maxWidth: 600, width: '90%', border: '1px solid rgba(252, 223, 104, 0.3)' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: '#fcdf68' }}>
              Debug Log:
            </Typography>
            {debugInfo.slice(-5).map((log, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                {log.message}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  }
  
  if (status === 'error') {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh"
        sx={{ bgcolor: '#f5f5f5', p: 3 }}
      >
        <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
          <Typography variant="h5" sx={{ color: '#d32f2f', fontWeight: 600, mb: 2 }}>
            Authentication Error
          </Typography>
          <Typography sx={{ mb: 3, color: '#666' }}>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/login')}
            sx={{ 
              bgcolor: '#173457',
              '&:hover': { bgcolor: '#0d1f33' }
            }}
          >
            Return to Login
          </Button>
          
          {/* Debug info in development */}
          {process.env.NODE_ENV === 'development' && debugInfo.length > 0 && (
            <Box sx={{ mt: 4, p: 2, bgcolor: '#fff', borderRadius: 1, textAlign: 'left' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                Debug Log:
              </Typography>
              {debugInfo.map((log, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem', mb: 0.5 }}>
                  [{new Date(log.timestamp).toLocaleTimeString()}] {log.message}
                  {Object.keys(log.data).length > 0 && (
                    <span style={{ color: '#666' }}> {JSON.stringify(log.data)}</span>
                  )}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  }
  
  return null;
}

/**
 * Handle invitation acceptance flow
 */
async function handleInvitationFlow(token, account, tokenResponse, addLog, navigate, setProfiles) {
  try {
    addLog('[InvitationFlow] Starting invitation acceptance', { token: token.substring(0, 10) + '...' });
    
    // Call accept-invitation API
    const response = await fetch(`${API_ENDPOINTS.auth}/users/accept-invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenResponse.accessToken}`
      },
      body: JSON.stringify({
        invitationToken: token,
        entraUserId: account.localAccountId,
        email: account.username,
        displayName: account.name
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(`Invitation acceptance failed: ${errorData.error || response.statusText}`);
    }
    
    const data = await response.json();
    addLog('[InvitationFlow] Invitation accepted successfully', { orgId: data.org?.id });
    
    // Clear ALL invitation and registration flags
    sessionStorage.removeItem('invitationToken');
    sessionStorage.removeItem('pendingInvitationAcceptance');
    sessionStorage.removeItem('needsOnboarding');
    sessionStorage.removeItem('postAuthRoute');
    sessionStorage.removeItem('selectedAccountType');
    sessionStorage.removeItem('registrationData');
    
    // Store profiles in both React state (via setProfiles) AND sessionStorage.
    // Writing only to sessionStorage is insufficient - UserContext React state may already
    // hold a different (stale) user's profile from an earlier login in the same tab session.
    setProfiles(data.user || null, data.org || null, data.subscriptionState || null);
    if (data.org) {
      sessionStorage.setItem('orgProfile', JSON.stringify(data.org));
    }
    if (data.user) {
      sessionStorage.setItem('userProfile', JSON.stringify(data.user));
    }
    
    // Route to auth/loading which will fetch profile and route to correct dashboard
    // Pass forceFetch: true so AuthLoading ignores any stale cached profile from a prior session
    addLog('[InvitationFlow] Routing to auth/loading for profile fetch');
    navigate('/auth/loading', { replace: true, state: { forceFetch: true } });
    
  } catch (err) {
    addLog('[InvitationFlow] ERROR', { message: err.message });
    console.error('[InvitationFlow] Error:', err);
    throw err; // Propagate to error UI
  }
}
